import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ICompany,
  ICompanyStatus,
  IDocument,
  ICompanyBranch,
  IEmployeeProfile,
} from "../types/company";

const initialState: ICompanyStatus = {
  total: 0,
  monthly_total: 0,
  companies: [],
  search: [],
  companyEmp: [],
  companyDoc: [],
  logs: [],
  loading: false,
  loadingCompanyId: null,
  company: null,
  last_page: 1,
  current_page: 1,
  branches: [],
  companyBranchUsers: [],
};

export const company = createSlice({
  name: "company",
  initialState,
  reducers: {
    setCompanies: (state, action) => {
      const payload = action.payload;
      // Handle different response structures
      if (Array.isArray(payload)) {
        // If payload is just an array
        state.companies = payload;
      } else if (payload.data) {
        // If payload has a data property with array and pagination info
        state.companies = Array.isArray(payload.data) ? payload.data : payload.data.data || [];
        state.last_page = payload.last_page || payload.data?.last_page || 1;
        state.current_page = payload.current_page || payload.data?.current_page || 1;
      } else {
        state.companies = [];
      }
    },
    createCompany: (state, action: PayloadAction<ICompany>) => {
      state.companies = [action.payload, ...state.companies];
    },
    deleteCompany: (state, action: PayloadAction<string>) => {
      state.companies = state.companies.filter(
        (company) => company.id !== action.payload
      );
    },
    setCompany: (state, action: PayloadAction<ICompany>) => {
      state.company = action.payload;
    },
    setCompanyDoc: (state, action) => {
      state.companyDoc = action.payload;
    },
    createDocument: (state, action: PayloadAction<IDocument>) => {
      state.companyDoc = [action.payload, ...state.companyDoc];
    },

    updateDocument: (state, action: PayloadAction<IDocument>) => {
      state.companyDoc = state.companyDoc.map((doc) =>
        doc.id === action.payload.id ? action.payload : doc
      );
    },

    deleteDocument: (state, action: PayloadAction<string>) => {
      state.companyDoc = state.companyDoc.filter(
        (doc) => doc.id !== action.payload
      );
    },

    setCompanyEmp: (state, action) => {
      state.companyEmp = action.payload;
    },

    setCompanyBranch: (state, action: PayloadAction<ICompanyBranch[]>) => {
      state.branches = action.payload;
    },
    setCompanyBranchUsers: (
      state,
      action: PayloadAction<IEmployeeProfile[]>
    ) => {
      state.companyBranchUsers = action.payload;
    },

    setNewCompanyBranchUser: (
      state,
      action: PayloadAction<IEmployeeProfile>
    ) => {
      state.companyBranchUsers = [action.payload, ...state.companyBranchUsers];
    },

    setSearch: (state, action) => {
      state.companies = action.payload.data;
    },
    setFileLogs: (state, action) => {
      state.logs = action.payload;
    },
    setCompanyLoading: (state) => {
      state.loading = true;
    },
    disableCompanyLoading: (state) => {
      state.loading = false;
    },

    // done
    setStatusUpdate: (state, action: PayloadAction<ICompany>) => {
      state.companies = state.companies.map((company) => {
        if (company.id === action.payload.id) return action.payload;
        return company;
      });
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setLoadingCompanyId: (state, action: PayloadAction<string | null>) => {
      state.loadingCompanyId = action.payload;
    },
  
    setTotal: (state, action: PayloadAction<number>) => {
      state.total = action.payload;
    },
    setMonthTotal: (state, action: PayloadAction<number>) => {
      state.monthly_total = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setSearch,
  setCompanyDoc,
  createDocument,
  deleteCompany,
  updateDocument,
  deleteDocument,
  setCompanyEmp,
  setCompany,
  setCompanyLoading,
  disableCompanyLoading,
  setCompanies,
  setCompanyBranch,
  setCompanyBranchUsers,
  setNewCompanyBranchUser,
  createCompany,
  setStatusUpdate,
  setFileLogs,
  setLoading,
  setLoadingCompanyId,
  setTotal,
  setMonthTotal,
} = company.actions;

export default company.reducer;
